import { useState, useEffect } from"react";
import axios from"axios";
import { useTranslation } from"react-i18next";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { Button } from"@/components/ui/button";
import { Input } from"@/components/ui/input";
import { Label } from"@/components/ui/label";
import { Badge } from"@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from"@/components/ui/dialog";
import { Progress } from"@/components/ui/progress";
import { DollarSign, TrendingUp, TrendingDown, AlertCircle, Edit, Save, X, BarChart3 } from"lucide-react";
import { toast } from"sonner";

const CostsPage = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [projects, setProjects] = useState([]);
 const [selectedProject, setSelectedProject] = useState("");
 const [currentProject, setCurrentProject] = useState(null);
 const [projectJobs, setProjectJobs] = useState([]);
 const [partners, setPartners] = useState([]);
 const [loading, setLoading] = useState(true);
 const [editingTaskId, setEditingTaskId] = useState(null);
 const [editPlannedCost, setEditPlannedCost] = useState("");

 const phaseNames = {
 1: t('projectProgress.phases.phase1'),
 2: t('projectProgress.phases.phase2'),
 3: t('projectProgress.phases.phase3'),
 4: t('projectProgress.phases.phase4')
 };

 const phaseWeights = {
 1: 10,
 2: 40,
 3: 40,
 4: 10
 };

 useEffect(() => {
 loadProjects();
 }, []);

 useEffect(() => {
 if (selectedProject) {
 loadProjectData();
 }
 }, [selectedProject]);

 const loadProjects = async () => {
 try {
 const response = await axios.get("/projects");
 setProjects(response.data);
 if (response.data.length > 0) {
 setSelectedProject(response.data[0].id);
 }
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 } finally {
 setLoading(false);
 }
 };

 const loadProjectData = async () => {
 setLoading(true);
 try {
 const [projectRes, jobsRes] = await Promise.all([
 axios.get(`/projects/${selectedProject}`),
 axios.get(`/project-progress/${selectedProject}`)
 ]);

 setCurrentProject(projectRes.data);
 setProjectJobs(jobsRes.data || []);
 } catch (error) {
 console.error("Failed to load project data", error);
 setProjectJobs([]);
 } finally {
 setLoading(false);
 }
 };

 const handleSavePlannedCost = async (taskId) => {
 const cost = parseFloat(editPlannedCost);
 if (isNaN(cost) || cost < 0) {
 toast.error(t("validation.required"));
 return;
 }

 try {
 await axios.patch(`/project-progress/${taskId}`, {
 planned_cost: cost
 });
 toast.success(t("messages.updateSuccess"));
 setEditingTaskId(null);
 loadProjectData();
 } catch (error) {
 toast.error(t("messages.saveFailed"));
 }
 };

 const getPartnerForTask = (taskId) => {
 const task = projectJobs.find(j => j.id === taskId);
 if (!task || !task.partner_id) return null;
 return partners.find(p => p.id === task.partner_id);
 };

 const calculatePhaseTotals = (phase) => {
 const phaseTasks = projectJobs.filter(j => j.phase === phase);
 
 const planned = phaseTasks.reduce((sum, task) => sum + (task.planned_cost || 0), 0);
 
 // For committed costs, only count main tasks (to avoid double-counting bundled offers)
 const committed = phaseTasks.reduce((sum, task) => {
 // Skip non-main bundled tasks
 if (task.is_bundled && task.main_task_id) {
 return sum;
 }
 const partner = getPartnerForTask(task.id);
 return sum + (partner?.contract_value || 0);
 }, 0);

 const actual = 0; // Will come from paid invoices later
 
 return { planned, committed, actual };
 };

 const calculateProjectTotals = () => {
 const planned = projectJobs.reduce((sum, task) => sum + (task.planned_cost || 0), 0);
 
 // For committed costs, only count main tasks (to avoid double-counting bundled offers)
 const committed = projectJobs.reduce((sum, task) => {
 // Skip non-main bundled tasks
 if (task.is_bundled && task.main_task_id) {
 return sum;
 }
 const partner = getPartnerForTask(task.id);
 return sum + (partner?.contract_value || 0);
 }, 0);

 const actual = 0; // Will come from paid invoices later

 return { planned, committed, actual };
 };

 const formatCurrency = (amount) => {
 return new Intl.NumberFormat('en-US', {
 style: 'currency',
 currency: 'EUR',
 minimumFractionDigits: 0,
 maximumFractionDigits: 0
 }).format(amount || 0);
 };

 const getVarianceColor = (planned, committed) => {
 if (!planned || planned === 0) return"text-slate-600";
 const variance = ((committed - planned) / planned) * 100;
 if (variance > 10) return"text-red-600";
 if (variance < -10) return"text-green-600";
 return"text-slate-600";
 };

 const projectTotals = selectedProject ? calculateProjectTotals() : { planned: 0, committed: 0, actual: 0 };

 if (loading && !selectedProject) {
 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="p-4">
 <div className="text-center py-12">Loading...</div>
 </div>
 </Layout>
 );
 }

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="space-y-6 p-6">
 {/* Header */}
 <div className="flex items-center justify-between">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900">{t('costs.title')}</h1>
 <p className="text-slate-500 mt-1">{t('costs.subtitle')}</p>
 </div>
 <Select value={selectedProject} onValueChange={setSelectedProject}>
 <SelectTrigger className="w-64">
 <SelectValue placeholder={t('common.selectProject')} />
 </SelectTrigger>
 <SelectContent>
 {projects.map((project) => (
 <SelectItem key={project.id} value={project.id}>
 {project.name}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>

 {/* Project Planned Budget Section */}
 {currentProject && (
 <Card className="">
 <CardHeader>
 <CardTitle className="flex items-center justify-between text-lg">
 <span>{t('projectProgress.projectBudgetArea')}</span>
 <Button size="sm"variant="outline"onClick={() => window.location.href = `/projects/${selectedProject}/progress`}>
 {t('costs.editBudget')} →
 </Button>
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
 <div className="bg-white p-3 rounded-sm">
 <div className="text-xs text-slate-600 mb-1">{t('projectProgress.totalArea')}</div>
 <div className="text-lg font-bold text-slate-900">
 {currentProject.total_m2 ? `${currentProject.total_m2.toLocaleString()} m²` : t('common.noData')}
 </div>
 </div>
 <div className="bg-white p-3 rounded-sm border-l-4 border-slate-200">
 <div className="text-xs text-slate-600 mb-1">{t('projectProgress.landCost')}</div>
 <div className="text-lg font-bold text-slate-900">
 {currentProject.land_cost ? `${currentProject.land_cost.toLocaleString()} ${currentProject.currency}` : t('common.noData')}
 </div>
 </div>
 <div className="bg-white p-3 rounded-sm border-l-4 border-slate-200">
 <div className="text-xs text-slate-600 mb-1">{t('projectProgress.constructionBudget')}</div>
 <div className="text-lg font-bold text-slate-900">
 {currentProject.planned_total_budget 
 ? `${currentProject.planned_total_budget.toLocaleString()} ${currentProject.currency}`
 : (currentProject.total_m2 && currentProject.cost_per_m2)
 ? `${(currentProject.total_m2 * currentProject.cost_per_m2).toLocaleString()} ${currentProject.currency}`
 : t('common.noData')}
 </div>
 </div>
 <div className="bg-white/50 p-3 rounded-sm border border-slate-200">
 <div className="text-xs text-slate-500 font-semibold mb-1">{t('projectProgress.totalProjectBudget').toUpperCase()}</div>
 <div className="text-xl font-bold text-slate-900">
 {(() => {
 const construction = currentProject.planned_total_budget || 
 (currentProject.total_m2 && currentProject.cost_per_m2 ? currentProject.total_m2 * currentProject.cost_per_m2 : 0);
 const land = currentProject.land_cost || 0;
 const total = construction + land;
 return total > 0 ? `${total.toLocaleString()} ${currentProject.currency}` : t('common.noData');
 })()}
 </div>
 <div className="text-xs text-gray-400 mt-1">{t('projectProgress.constructionBudget')} + {t('projectProgress.landCost')}</div>
 </div>
 </div>
 </CardContent>
 </Card>
 )}

 {/* Summary Cards */}
 <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center gap-3 mb-2">
 <DollarSign className="h-6 w-6 text-blue-600"/>
 <div className="text-sm text-slate-500 font-semibold">{t('costs.plannedBudget')}</div>
 </div>
 <div className="text-2xl font-semibold tabular-nums text-slate-900">{formatCurrency(projectTotals.planned)}</div>
 <div className="text-xs text-slate-600 mt-1">{t('costs.estimatedCost')}</div>
 </CardContent>
 </Card>

 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center gap-3 mb-2">
 <TrendingUp className="h-6 w-6 text-amber-600"/>
 <div className="text-sm text-slate-500 font-semibold">{t('costs.committedOffers')}</div>
 </div>
 <div className="text-2xl font-semibold tabular-nums text-slate-900">{formatCurrency(projectTotals.committed)}</div>
 <div className="text-xs text-slate-600 mt-1">{t('costs.fromAcceptedOffers')}</div>
 </CardContent>
 </Card>

 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center gap-3 mb-2">
 <AlertCircle className="h-6 w-6 text-emerald-600"/>
 <div className="text-sm text-slate-500 font-semibold">{t('costs.actualSpent')}</div>
 </div>
 <div className="text-2xl font-semibold tabular-nums text-slate-900">{formatCurrency(projectTotals.actual)}</div>
 <div className="text-xs text-slate-600 mt-1">{t('costs.realExpenses')}</div>
 </CardContent>
 </Card>
 </div>

 {/* Budget Utilization Visualization */}
 {projectTotals.planned > 0 && (
 <Card className="">
 <CardHeader className="pb-2">
 <CardTitle className="flex items-center gap-2 text-lg">
 <BarChart3 className="w-5 h-5 text-indigo-600"/>
 {t('costs.budgetUtilization')}
 </CardTitle>
 </CardHeader>
 <CardContent className="space-y-6">
 {/* Overall Budget Progress */}
 <div className="space-y-3">
 <div className="flex items-center justify-between text-sm">
 <span className="font-medium text-[#334155]">{t('costs.committedVsPlanned')}</span>
 <span className={`font-bold ${
 (projectTotals.committed / projectTotals.planned) > 1 
 ? 'text-red-600' 
 : (projectTotals.committed / projectTotals.planned) > 0.9 
 ? 'text-amber-600' 
 : 'text-green-600'
 }`}>
 {((projectTotals.committed / projectTotals.planned) * 100).toFixed(1)}%
 </span>
 </div>
 <div className="relative h-6 bg-white/60 rounded-full overflow-hidden">
 <div 
 className={`absolute h-full rounded-full transition-all duration-500 ${
 (projectTotals.committed / projectTotals.planned) > 1 
 ? 'bg-gradient-to-r from-red-400 to-red-600' 
 : (projectTotals.committed / projectTotals.planned) > 0.9 
 ? 'bg-gradient-to-r from-amber-400 to-amber-600' 
 : 'bg-gradient-to-r from-emerald-400 to-emerald-600'
 }`}
 style={{ width: `${Math.min((projectTotals.committed / projectTotals.planned) * 100, 100)}%` }}
 />
 {(projectTotals.committed / projectTotals.planned) > 1 && (
 <div 
 className="absolute h-full bg-red-700 opacity-50"
 style={{ 
 left: '100%', 
 width: `${Math.min(((projectTotals.committed / projectTotals.planned) - 1) * 100, 50)}%` 
 }}
 />
 )}
 </div>
 <div className="flex justify-between text-[11px] text-slate-500">
 <span>€0</span>
 <span className="font-medium">{formatCurrency(projectTotals.planned)} (Budget)</span>
 </div>
 </div>

 {/* Phase-by-Phase Budget Breakdown */}
 <div className="pt-4 border-t">
 <h4 className="text-sm font-semibold text-[#334155] mb-4">Budget Utilization by Phase</h4>
 <div className="space-y-4">
 {[1, 2, 3, 4].map(phase => {
 const phaseTotals = calculatePhaseTotals(phase);
 const phaseTasks = projectJobs.filter(j => j.phase === phase);
 if (phaseTasks.length === 0 || phaseTotals.planned === 0) return null;
 
 const utilizationPercent = (phaseTotals.committed / phaseTotals.planned) * 100;
 const isOverBudget = utilizationPercent > 100;
 const isNearBudget = utilizationPercent > 90;
 
 return (
 <div key={phase} className="space-y-2">
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-2">
 <Badge variant="outline"className="text-xs">Phase {phase}</Badge>
 <span className="text-sm text-slate-500">{phaseNames[phase]}</span>
 </div>
 <div className="flex items-center gap-3 text-sm">
 <span className="text-slate-500">{formatCurrency(phaseTotals.committed)}</span>
 <span className="text-slate-400">/</span>
 <span className="text-[#334155] font-medium">{formatCurrency(phaseTotals.planned)}</span>
 <Badge className={`text-xs ${
 isOverBudget 
 ? 'bg-rose-500/10 text-rose-700 border-red-200/60' 
 : isNearBudget 
 ? 'bg-amber-100 text-amber-700 border-amber-200/60'
 : 'bg-emerald-500/10 text-emerald-700 border-green-200/60'
 }`}>
 {utilizationPercent.toFixed(0)}%
 </Badge>
 </div>
 </div>
 <div className="relative h-3 bg-white/60 rounded-full overflow-hidden">
 <div 
 className={`absolute h-full rounded-full transition-all duration-300 ${
 isOverBudget 
 ? 'bg-red-500' 
 : isNearBudget 
 ? 'bg-amber-500' 
 : 'bg-emerald-500'
 }`}
 style={{ width: `${Math.min(utilizationPercent, 100)}%` }}
 />
 </div>
 </div>
 );
 })}
 </div>
 </div>

 {/* Budget Health Indicator */}
 <div className="pt-4 border-t">
 <div className="flex items-center justify-between p-4 rounded-sm bg-white/50">
 <div className="flex items-center gap-3">
 <div className={`w-4 h-4 rounded-full ${
 (projectTotals.committed / projectTotals.planned) > 1 
 ? 'bg-red-500' 
 : (projectTotals.committed / projectTotals.planned) > 0.9 
 ? 'bg-amber-500' 
 : 'bg-green-500'
 }`} />
 <div>
 <div className="font-semibold text-slate-900">
 {(projectTotals.committed / projectTotals.planned) > 1 
 ? 'Over Budget' 
 : (projectTotals.committed / projectTotals.planned) > 0.9 
 ? 'Near Budget Limit' 
 : 'Within Budget'}
 </div>
 <div className="text-sm text-slate-600">
 {(projectTotals.committed / projectTotals.planned) > 1 
 ? `Exceeding budget by ${formatCurrency(projectTotals.committed - projectTotals.planned)}`
 : `${formatCurrency(projectTotals.planned - projectTotals.committed)} remaining in budget`}
 </div>
 </div>
 </div>
 </div>
 </div>
 </CardContent>
 </Card>
 )}

 {/* Variance Alert */}
 {projectTotals.planned > 0 && projectTotals.committed > projectTotals.planned && (
 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center gap-3">
 <AlertCircle className="h-7 w-7 text-rose-600"/>
 <div>
 <div className="font-semibold text-slate-900">Budget Overrun Warning</div>
 <div className="text-sm text-slate-600 mt-1">
 Committed costs exceed planned budget by {formatCurrency(projectTotals.committed - projectTotals.planned)}
 ({(((projectTotals.committed - projectTotals.planned) / projectTotals.planned) * 100).toFixed(1)}%)
 </div>
 </div>
 </div>
 </CardContent>
 </Card>
 )}

 {/* Phase Breakdown */}
 {loading ? (
 <div className="text-center py-12">Loading project data...</div>
 ) : (
 <div className="space-y-4">
 {[1, 2, 3, 4].map(phase => {
 const phaseTasks = projectJobs.filter(j => j.phase === phase);
 if (phaseTasks.length === 0) return null;

 const phaseTotals = calculatePhaseTotals(phase);

 return (
 <Card key={phase} className="border-2">
 <CardHeader className="bg-white/50">
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-3">
 <CardTitle className="text-lg">
 Phase {phase} – {phaseNames[phase]}
 </CardTitle>
 <Badge variant="outline"className="bg-blue-500/10 text-blue-700">
 {phaseWeights[phase]}% of project
 </Badge>
 </div>
 <div className="flex items-center gap-6 text-sm">
 <div className="text-right">
 <div className="text-xs text-slate-600">Planned</div>
 <div className="font-bold text-blue-700">{formatCurrency(phaseTotals.planned)}</div>
 </div>
 <div className="text-right">
 <div className="text-xs text-slate-600">Committed</div>
 <div className="font-bold text-orange-700">{formatCurrency(phaseTotals.committed)}</div>
 </div>
 <div className="text-right">
 <div className="text-xs text-slate-600">Actual</div>
 <div className="font-bold text-green-700">{formatCurrency(phaseTotals.actual)}</div>
 </div>
 </div>
 </div>
 </CardHeader>
 <CardContent className="pt-4">
 <div className="space-y-2">
 {phaseTasks.map(task => {
 const partner = getPartnerForTask(task.id);
 const isEditing = editingTaskId === task.id;
 const isBundled = task.is_bundled;
 const isMainTask = !task.main_task_id; // If no main_task_id, this IS the main task
 const mainTask = task.main_task_id ? projectJobs.find(j => j.id === task.main_task_id) : null;

 return (
 <div key={task.id} className={`flex items-center justify-between p-3 border rounded hover:bg-white/80 ${isBundled && !isMainTask ? 'border-slate-200 bg-white' : 'border-slate-200'}`}>
 <div className="flex-1">
 <div className="flex items-center gap-2">
 <div className="font-medium text-slate-900">{task.job_name}</div>
 {isBundled && isMainTask && (
 <Badge className="bg-blue-600 text-white text-xs">Bundled Offer</Badge>
 )}
 </div>
 {partner && (
 <div className="text-[11px] text-slate-500 mt-1">
 Partner: {partner.company_name}
 </div>
 )}
 {isBundled && !isMainTask && mainTask && (
 <div className="text-xs text-blue-600 mt-1">
 ↪ Cost included in &ldquo;{mainTask.job_name}&rdquo;
 </div>
 )}
 </div>
 <div className="flex items-center gap-6">
 {/* Planned Cost */}
 <div className="text-right w-28">
 {isEditing ? (
 <div className="flex items-center gap-1">
 <Input
 type="number"
 value={editPlannedCost}
 onChange={(e) => setEditPlannedCost(e.target.value)}
 className="w-24 h-8 text-sm"
 autoFocus
 />
 </div>
 ) : (
 <>
 <div className="text-xs text-slate-600">Planned</div>
 <div className="font-semibold text-blue-700">
 {task.planned_cost ? formatCurrency(task.planned_cost) :"—"}
 </div>
 </>
 )}
 </div>

 {/* Committed Cost */}
 <div className="text-right w-28">
 <div className="text-xs text-slate-600">Committed</div>
 <div className="font-semibold text-orange-700">
 {isBundled && !isMainTask ? (
 <span className="text-[11px] text-slate-500">See main</span>
 ) : (
 partner?.contract_value ? formatCurrency(partner.contract_value) :"—"
 )}
 </div>
 </div>

 {/* Actual Cost */}
 <div className="text-right w-28">
 <div className="text-xs text-slate-600">Actual</div>
 <div className="font-semibold text-green-700">—</div>
 </div>

 {/* Actions */}
 <div className="w-20">
 {isEditing ? (
 <div className="flex gap-1">
 <Button size="sm"onClick={() => handleSavePlannedCost(task.id)} className="h-7 px-2">
 <Save className="w-3 h-3"/>
 </Button>
 <Button size="sm"variant="outline"onClick={() => setEditingTaskId(null)} className="h-7 px-2">
 <X className="w-3 h-3"/>
 </Button>
 </div>
 ) : (
 <Button
 size="sm"
 variant="ghost"
 onClick={() => {
 setEditingTaskId(task.id);
 setEditPlannedCost(task.planned_cost ||"");
 }}
 className="h-7"
 >
 <Edit className="w-3 h-3"/>
 </Button>
 )}
 </div>
 </div>
 </div>
 );
 })}
 </div>
 </CardContent>
 </Card>
 );
 })}
 </div>
 )}
 </div>
 </Layout>
 );
};

export default CostsPage;
